/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, Input, ViewChild } from "@angular/core";
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, Observer } from 'rxjs';

import { Dialog } from "../../../models/dialog";
import { Rule } from "../../../models/rule";
import { RuleCondition, Operator, OperatorUtil, Type } from "../../../models/rule-condition";
import { Property } from '../../../models/property';
import { PropertyType } from '../../../models/property-type';
import { EntityType } from '../../../models/entity-type';

import { DialogService } from "../../../services/dialog.service";
import { EntityService } from "../../../services/entity.service";
import { BaseInstance } from "app/models/base-instance";
import { switchMap } from "rxjs/operators";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";

const PROP_DIALOG_NAME = "dialog_name";
const PROP_DIALOG_BUTTON = "button_code";
const PROP_DIALOG_ID = "dialog_id";

@Component({
  selector: 'app-dialog-parameter-editor',
  templateUrl: './dialog-parameter-editor.component.html'
})
export class DialogParameterEditorComponent implements OnInit {

    /** Reference to the SimpleSuccessErrorDialog used to display error */
    @ViewChild('successErrorDialog') 
    simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

    @Input()
    rule: Rule;

    mDialogNameCondition: RuleCondition = null;
    
    dialog: Dialog;
    
    typeaheadValue: string;
    typeaheadLoading: boolean;
    typeaheadNoResults: boolean;
    entities: Observable<BaseInstance[]>;
    
    buttonId = '-1';
    
    constructor(
        private dialogService: DialogService,
        private entityService: EntityService
    ) {}
    
    ngOnInit(): void {
        console.log("ngOnInit()");
        //Load / Create the Dialog Name Condition...
        this.mDialogNameCondition = this.rule.conditions.find(condition => condition.property == PROP_DIALOG_ID);
        if (this.mDialogNameCondition == null) {
            const condition: RuleCondition = new RuleCondition();
            condition.source = PROP_DIALOG_ID;
            condition.sourceType = Type.EVENT;
            condition.property = PROP_DIALOG_ID;
            condition.propertyType = PropertyType.LONG;
            condition.operator = Operator.EQUAL;

            this.mDialogNameCondition = condition;
            this.rule.conditions.push(this.mDialogNameCondition);
        }

        //Update the Button Condition value
        const buttonChangeCondition = this.rule.conditions.find(condition => condition.property == PROP_DIALOG_BUTTON);
        this.buttonId = buttonChangeCondition == null? '-1' : buttonChangeCondition.propertyValue.toString();

        console.log("Button ID: " + this.buttonId);

        //If the Property Value for the Dialog Name Condition is null, set it to the empty string. This is needed since the "Type Ahead" look-up requires a value.
        if (this.mDialogNameCondition.propertyValue == null) {
            this.mDialogNameCondition.propertyValue = "";
        } else {
            this.loadDialogForId(Number(this.mDialogNameCondition.propertyValue));
        }

        //Subscribe to changes on the Dialog Name Condition's Property Value to update the Type Ahead Values
        //KHT 08/17/2021 - updated to new ngx-bootstrap standard
        this.entities = new Observable((observer: Observer<string>) => {
            observer.next(this.typeaheadValue);
        }).pipe(
            switchMap((query: string) => {
                if(query){
                    return this.search(query);
                }
            })
        )
    }

    async loadDialogForName(dialogName: string): Promise<void> {
        try {
            const dialogInstances = await this.dialogService.getAllDialogs();
            const foundDialog = dialogInstances == null? null : dialogInstances.find(d => d.name == dialogName);
            this.dialog = foundDialog == null? null : await this.dialogService.getDialogById(foundDialog.id);
        } catch (error) {
            this.handleError(error);
        }
    }
    
    async loadDialogForId(dialogId: number): Promise<void> {
        try {
            this.dialog = await this.dialogService.getDialogById(dialogId);
			this.typeaheadValue = this.dialog.name;
        } catch (error) {
            this.handleError(error);
        }
    }
    
    changeTypeaheadLoading(e: boolean): void {
        this.typeaheadLoading = e;
    }
    
    typeaheadOnSelect(e: TypeaheadMatch): void {
        const selectedItem = e.item;
        if (selectedItem != null) {
            const selectedDialog = selectedItem as BaseInstance;
            if (selectedDialog.name != null) {
                this.mDialogNameCondition.propertyValue = e.item.id;
                this.loadDialogForId(e.item.id);
            }
        }
    }

    search(token: string): Observable<BaseInstance[]>  {
        return this.entityService.searchByEntity(EntityType.DIALOG, token);
    }
    
    onButtonChange(buttonId: string): void {
        const index = this.rule.conditions.findIndex(condition => condition.property == "button_code");
        console.log("onButtonChange() - Button ID: '" + buttonId + "' (" + this.buttonId + "), Index: '" + index + "'...");
        
        if (buttonId == "-1" && index > -1) {
            //If the Button ID is "-1" (Any Button) then remove the Button Condition if it is part of the Rule
            this.rule.conditions.splice(index, 1);
        } else if (index > -1) {
            //Button ID is not "-1" so it is set to an actual button and there is already a Button Condition defined on the Rule... Just update it's value to the new Button
            this.rule.conditions[index].propertyValue = buttonId; 
        } else {
            //Button ID is not "-1" so it is set to an actual button, but there is no Button Condition defined for the Rule... Create one and add it to the Rule's Conditions
            const condition: RuleCondition = new RuleCondition();
            condition.source = "button_code";
            condition.sourceType = Type.EVENT;
            condition.property = "button_code";
            condition.propertyType = PropertyType.STRING;
            condition.operator = Operator.EQUAL;
            condition.propertyValue = buttonId;
            this.rule.conditions.push(condition);
        }
    }

    handleError(error: any): void {
      console.error("handleError() - Error: " + error);
      if (error != null) {
        const response: Response = error;
        this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
      }
    }

}

